import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatCents, formatPercent, formatQuantity, offerTotals, TAX_RATES, type OfferLine, type OfferTotals, type ProductOption, type ProductUnit, type TaxRate } from '@kontor/shared';
import { api } from '../../api';
import { bpText, centsText, milliText, parseBp, parseCents, parseMilli } from '../../money';
import { ProductPicker } from './ProductPicker';
import './offers.css';

/**
 * Positionen mit Summen, gemeinsam für Angebote (KON-098) und Aufträge (KON-125). Der Editor arbeitet mit Texteingaben (LineDraft), die Umrechnung in Cent,
 * Tausendstel und Basispunkte übernimmt parseDrafts. Die Summen rechnet dieselbe Funktion wie der Server (offerTotals).
 */

export interface LineDraft {
  key: string;
  itemId: string | null;
  title: string;
  description: string;
  qty: string;
  unit: string;
  price: string;
  discount: string;
  taxRate: TaxRate;
}

let counter = 0;
const newKey = () => `l${++counter}`;
export const emptyLine = (unit = 'Stk.'): LineDraft => ({ key: newKey(), itemId: null, title: '', description: '', qty: '1', unit, price: '', discount: '', taxRate: 19 });
export const draftFromLine = (l: OfferLine): LineDraft => ({ key: newKey(), itemId: l.itemId, title: l.title, description: l.description ?? '', qty: milliText(l.quantityMilli), unit: l.unit, price: centsText(l.unitPriceCents), discount: bpText(l.discountBp), taxRate: l.taxRate });
export const draftFromProduct = (p: ProductOption): LineDraft => ({ key: newKey(), itemId: p.id, title: p.name, description: p.description ?? '', qty: '1', unit: p.unitSymbol, price: centsText(p.salePriceCents), discount: '', taxRate: p.taxRate });

export function parseDrafts(lines: LineDraft[]) {
  return lines.map((l) => ({ quantityMilli: parseMilli(l.qty), unitPriceCents: parseCents(l.price), discountBp: parseBp(l.discount || '0'), taxRate: l.taxRate }));
}

/** Positionen für den Aufruf der API */
export function linesBody(lines: LineDraft[]) {
  const parsed = parseDrafts(lines);
  return lines.map((l, i) => ({ itemId: l.itemId, title: l.title.trim(), description: l.description.trim() || null, quantityMilli: parsed[i]!.quantityMilli, unit: l.unit.trim(), unitPriceCents: parsed[i]!.unitPriceCents, discountBp: parsed[i]!.discountBp ?? 0, taxRate: l.taxRate }));
}

/** Fehlertext zur ersten unvollständigen Position oder null */
export function linesProblem(lines: LineDraft[], unitSymbols: string[] | null): string | null {
  const parsed = parseDrafts(lines);
  const bad = parsed.findIndex((p, i) => p.quantityMilli === null || p.quantityMilli <= 0 || p.unitPriceCents === null || !lines[i]!.title.trim());
  if (bad >= 0) return `Position ${bad + 1}: Bitte Bezeichnung, eine Menge größer als 0 und einen Preis angeben.`;
  const badUnit = unitSymbols ? lines.findIndex((l) => !unitSymbols.includes(l.unit)) : -1;
  if (badUnit >= 0) return `Position ${badUnit + 1}: Bitte eine Einheit aus der Auswahl wählen.`;
  return null;
}

/** Einheiten aus der Einrichtung (KON-114), die Position wählt nur aus ihnen */
export function useUnits() {
  const units = useQuery({ queryKey: ['product-units', 'options'], queryFn: async () => (await api<{ data: ProductUnit[] }>('GET', '/product-units/options')).data, staleTime: 5 * 60_000 });
  const symbols = (units.data ?? []).map((u) => u.symbol);
  return { units: units.data ?? null, symbols, defaultUnit: symbols.includes('Stk.') ? 'Stk.' : (symbols[0] ?? 'Stk.') };
}

interface EditorProps {
  lines: LineDraft[];
  setLines: (update: (lines: LineDraft[]) => LineDraft[]) => void;
  discount: string;
  setDiscount: (value: string) => void;
  smallBusiness: boolean;
  /** Neue Positionen ohne gültige Einheit bekommen die Vorgabe, sobald die Einheiten geladen sind */
  fixUnits?: boolean;
}

export function LinesEditor({ lines, setLines, discount, setDiscount, smallBusiness, fixUnits = false }: EditorProps) {
  const { units, symbols, defaultUnit } = useUnits();
  const parsed = parseDrafts(lines);
  const discountBp = parseBp(discount || '0');
  const totals = useMemo(
    () => offerTotals(parsed.map((p) => ({ quantityMilli: p.quantityMilli ?? 0, unitPriceCents: p.unitPriceCents ?? 0, discountBp: p.discountBp ?? 0, taxRate: p.taxRate })), discountBp ?? 0, smallBusiness),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(parsed), discountBp, smallBusiness],
  );

  useEffect(() => {
    if (!units?.length || !fixUnits) return;
    setLines((ls) => ls.map((l) => (symbols.includes(l.unit) || l.itemId ? l : { ...l, unit: defaultUnit })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, fixUnits, lines.length]);

  const setLine = (key: string, patch: Partial<LineDraft>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const move = (index: number, delta: -1 | 1) =>
    setLines((ls) => {
      const next = [...ls];
      const target = index + delta;
      if (target < 0 || target >= next.length) return ls;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });

  return (
    <>
      <div className="offer-lines">
        {lines.map((l, i) => {
          const p = parsed[i]!;
          return (
            <div key={l.key} className="offer-line">
              <span className="offer-line__pos" aria-hidden="true">{i + 1}</span>
              <div className="offer-line__main">
                <input className="input" value={l.title} onChange={(e) => setLine(l.key, { title: e.target.value })} placeholder="Bezeichnung" aria-label={`Bezeichnung Position ${i + 1}`} maxLength={200} />
                <textarea className="input textarea textarea--line" value={l.description} onChange={(e) => setLine(l.key, { description: e.target.value })} placeholder="Beschreibung (freiwillig)" aria-label={`Beschreibung Position ${i + 1}`} maxLength={2000} />
              </div>
              <label className="offer-line__cell">
                <span>Menge</span>
                <input className="input num" inputMode="decimal" value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} aria-invalid={p.quantityMilli === null || p.quantityMilli <= 0} />
              </label>
              <label className="offer-line__cell">
                <span>Einheit</span>
                <select className="input" value={l.unit} onChange={(e) => setLine(l.key, { unit: e.target.value })} aria-invalid={!!units && !symbols.includes(l.unit)}>
                  {!symbols.includes(l.unit) && <option value={l.unit}>{l.unit ? `${l.unit} (nicht in der Einrichtung)` : 'Bitte wählen'}</option>}
                  {units?.map((u) => <option key={u.id} value={u.symbol} title={u.name}>{u.symbol}</option>)}
                </select>
              </label>
              <label className="offer-line__cell">
                <span>Einzelpreis €</span>
                <input className="input num" inputMode="decimal" value={l.price} onChange={(e) => setLine(l.key, { price: e.target.value })} placeholder="0,00" aria-invalid={p.unitPriceCents === null} />
              </label>
              <label className="offer-line__cell">
                <span>Rabatt %</span>
                <input className="input num" inputMode="decimal" value={l.discount} onChange={(e) => setLine(l.key, { discount: e.target.value })} placeholder="0" />
              </label>
              <label className="offer-line__cell">
                <span>USt.</span>
                <select className="input" value={l.taxRate} onChange={(e) => setLine(l.key, { taxRate: Number(e.target.value) as TaxRate })} disabled={smallBusiness}>
                  {TAX_RATES.map((r) => <option key={r} value={r}>{r} %</option>)}
                </select>
              </label>
              <div className="offer-line__amount">
                <span className="muted small">Betrag</span>
                <strong>{formatCents(totals.lineCents[i] ?? 0)}</strong>
              </div>
              <div className="offer-line__tools">
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Position ${i + 1} nach oben`}>↑</button>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => move(i, 1)} disabled={i === lines.length - 1} aria-label={`Position ${i + 1} nach unten`}>↓</button>
                <button type="button" className="btn btn--danger btn--sm" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} aria-label={`Position ${i + 1} entfernen`}>Entfernen</button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="offer-editor__add">
        <ProductPicker onPick={(p) => setLines((ls) => [...ls.filter((l) => l.title || l.price), draftFromProduct(p)])} />
        <button type="button" className="btn btn--secondary" onClick={() => setLines((ls) => [...ls, emptyLine(defaultUnit)])}>Freie Position</button>
      </div>

      <div className="offer-totals">
        <label className="field offer-totals__discount">
          <span>Rabatt auf alles %</span>
          <input className="input num" inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0" aria-invalid={discountBp === null} />
        </label>
        <dl>
          {totals.discountCents !== 0 && (
            <>
              <dt>Summe der Positionen</dt>
              <dd>{formatCents(totals.linesCents)}</dd>
              <dt>abzüglich {formatPercent(discountBp ?? 0)}</dt>
              <dd>– {formatCents(totals.discountCents)}</dd>
            </>
          )}
          <dt>Netto</dt>
          <dd>{formatCents(totals.netCents)}</dd>
          {!smallBusiness &&
            totals.groups.filter((g) => g.rate > 0).map((g) => (
              <SumRow key={g.rate} label={`${g.rate} % USt.`} value={formatCents(g.taxCents)} />
            ))}
          <dt className="offer-totals__gross">Gesamt brutto</dt>
          <dd className="offer-totals__gross">{formatCents(totals.grossCents)}</dd>
        </dl>
        {smallBusiness && <p className="muted small">Kleinunternehmer nach § 19 UStG: keine Umsatzsteuer.</p>}
      </div>
    </>
  );
}

function SumRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/** Positionen mit Summen zur Ansicht, im Angebot und im Auftrag */
export function LinesView({ lines, totals, discountBp, smallBusiness }: { lines: OfferLine[]; totals: OfferTotals; discountBp: number; smallBusiness: boolean }) {
  return (
    <>
      <table className="table table--cards offer-table">
        <thead>
          <tr>
            <th>Pos.</th>
            <th>Bezeichnung</th>
            <th className="num">Menge</th>
            <th className="num">Einzelpreis</th>
            <th className="num">Rabatt</th>
            <th className="num">USt.</th>
            <th className="num">Betrag</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id}>
              <td data-label="Pos.">{l.position}</td>
              <td>
                <strong>{l.title}</strong>
                {l.description && <div className="muted small offer-desc">{l.description}</div>}
              </td>
              <td data-label="Menge" className="num">{formatQuantity(l.quantityMilli)} {l.unit}</td>
              <td data-label="Einzelpreis" className="num">{formatCents(l.unitPriceCents)}</td>
              <td data-label="Rabatt" className="num">{l.discountBp ? formatPercent(l.discountBp) : '–'}</td>
              <td data-label="USt." className="num">{smallBusiness ? '–' : `${l.taxRate} %`}</td>
              <td data-label="Betrag" className="num">{formatCents(l.amountCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <dl className="offer-sum">
        {totals.discountCents !== 0 && (
          <>
            <dt>Summe der Positionen</dt>
            <dd>{formatCents(totals.linesCents)}</dd>
            <dt>abzüglich {formatPercent(discountBp)} Rabatt</dt>
            <dd>– {formatCents(totals.discountCents)}</dd>
          </>
        )}
        <dt>Netto</dt>
        <dd>{formatCents(totals.netCents)}</dd>
        {!smallBusiness &&
          totals.groups.filter((g) => g.rate > 0).map((g) => (
            <div key={g.rate} className="offer-sum__row">
              <dt>{g.rate} % USt. auf {formatCents(g.netCents)}</dt>
              <dd>{formatCents(g.taxCents)}</dd>
            </div>
          ))}
        <dt className="offer-totals__gross">Gesamt brutto</dt>
        <dd className="offer-totals__gross">{formatCents(totals.grossCents)}</dd>
      </dl>
      {smallBusiness && <p className="muted small">Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.</p>}
    </>
  );
}
